import { type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { interestPrompt } from '../lib/guestPrompts'
import { logInButtonLabel } from '../lib/commonStrings'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Override the default copy with action-specific context, e.g.
  // "Sign up to create a group" / "Sign up to mark interests".
  title?: ReactNode
  description?: ReactNode
  // After successful sign-up, land here instead of returning to the current
  // location. Use to resume the user's intent (e.g. open the create-group
  // dialog by routing through `/?create=group`).
  afterSignIn?: { pathname: string; search?: string; hash?: string }
  // Fired before the dialog closes on the way to /login, so the provider can
  // tell an acceptance apart from a dismissal.
  onAccept?: () => void
}

export function GuestSignInDialog({ open, onOpenChange, onAccept, title, description, afterSignIn }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const from = afterSignIn
    ? { pathname: afterSignIn.pathname, search: afterSignIn.search ?? '', hash: afterSignIn.hash ?? '' }
    : location
  const defaults = interestPrompt()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title ?? defaults.title}</DialogTitle>
          <DialogDescription>{description ?? defaults.description}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            {fbt('Not now', 'Button to dismiss the sign-up upsell dialog')}
          </Button>
          <Button
            className="cursor-pointer"
            onClick={() => {
              onAccept?.()
              onOpenChange(false)
              navigate('/login', { state: { from } })
            }}
          >
            {logInButtonLabel()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
